import { useParams, Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import MovieCard from '@/components/base/MovieCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === id);

  if (!movie) {
    return <NotFound />;
  }

  const relatedMovies = movies
    .filter((m) => m.categorySlug === movie.categorySlug && m.id !== movie.id)
    .slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.imageUrl,
    dateCreated: movie.year,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map((name) => ({
      '@type': 'Person',
      name,
    })),
    duration: movie.duration,
    countryOfOrigin: {
      '@type': 'Country',
      name: movie.region,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.6080cinema.com' },
      { '@type': 'ListItem', position: 2, name: movie.category, item: `https://www.6080cinema.com/category/${movie.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: movie.title },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <div className="w-full px-4 md:px-6 py-4">
        <div className="max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: movie.category, href: `/category/${movie.categorySlug}` },
              { label: movie.title },
            ]}
          />

          <article className="flex flex-col lg:flex-row gap-8 md:gap-10 mt-6">
            <div className="lg:w-[350px] flex-shrink-0">
              <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={movie.imageUrl}
                  alt={movie.title}
                  title={`${movie.title} - 6080影院电影大全`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <header>
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-2.5 py-1 bg-accent-500 text-background-50 text-sm rounded font-semibold whitespace-nowrap">
                    {movie.rating}
                  </span>
                  {movie.featured && (
                    <span className="px-2.5 py-1 bg-primary-500/10 text-primary-600 text-sm rounded whitespace-nowrap">
                      编辑推荐
                    </span>
                  )}
                  <span className="text-sm text-foreground-500">{movie.category}</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 font-heading">{movie.title}</h1>
              </header>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6 p-5 bg-background-100 rounded-lg">
                <div>
                  <p className="text-xs text-foreground-500 mb-0.5">导演</p>
                  <p className="text-sm font-medium text-foreground-900">{movie.director}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-0.5">主演</p>
                  <p className="text-sm font-medium text-foreground-900">{movie.actors.join(' / ')}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-0.5">年份</p>
                  <p className="text-sm font-medium text-foreground-900">{movie.year}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-0.5">片长</p>
                  <p className="text-sm font-medium text-foreground-900">{movie.duration}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-0.5">地区</p>
                  <p className="text-sm font-medium text-foreground-900">{movie.region}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-0.5">评分</p>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 text-sm"></i>
                    <span className="text-sm font-medium text-foreground-900">{movie.rating}</span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-base font-semibold text-foreground-950 mb-3 font-heading">剧情简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{movie.description}</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button className="px-6 py-3 bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer">
                  <i className="ri-play-circle-line mr-2"></i>立即播放
                </button>
                <button className="px-6 py-3 bg-background-100 border border-background-300/60 text-foreground-700 rounded-md hover:bg-background-200 transition-colors text-sm whitespace-nowrap cursor-pointer">
                  <i className="ri-heart-line mr-2"></i>加入收藏
                </button>
              </div>
            </div>
          </article>

          {relatedMovies.length > 0 && (
            <section className="mt-14 md:mt-16">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading">
                  同类推荐
                </h2>
                <Link
                  to={`/category/${movie.categorySlug}`}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  更多{movie.category} <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}